import { createSelector, createSlice } from '@reduxjs/toolkit';
import memoize from 'lodash/memoize';
import values from 'lodash/values';
import { createCodesThunk } from '../api/thunk.helper';
import { formatUomArrayItems, pluck } from '../helpers/array.helpers';
import { getCreateSubscriberRequirements } from '../helpers/customer.requirements.helpers';
import {
  CodeTableAdditionalPropertyTypeOf,
  CodeTableNumber,
  CodeTableState,
  CoreState,
  MetadataCodeRecordWithAdditionalPropertiesDictionary,
  MetadataCodeTypeOf,
  MetadataCodeWithAdditionalPropertiesDictionary
} from '../interfaces';
import { ActionCache } from '../utility/actionCache';
import {
  BalanceUnitCategory,
  BYTE_ID,
  DEFAULT_CODE_TABLE_COLUMN_VALUES
} from './codeTables.constants';

//Actions
const fetchCodeThunk = createCodesThunk();
const cache = new ActionCache();

/**
 * Load a code table if it is not already loaded, return cached promise
 */
export function loadCodeTable(id: CodeTableNumber) {
  return async (dispatch: Function) => {
    return cache.Load(id, dispatch, fetchCodeThunk);
  };
}

const slice = createSlice({
  name: 'codeTables',
  initialState: {
    tables: {}
  } as CodeTableState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchCodeThunk.fulfilled, (state, action) => {
      const id = action.meta.arg;
      // @ts-ignore TODO: fix this type error
      state.tables[id] = action.payload.Codes;
    });
  }
});

export default slice.reducer;

//Selectors
const getAllCodeTables = (state: CoreState) => state.core.codeTables.tables;

export const getCodeTable = memoize(
  <T extends CodeTableNumber>(codeTableId: T) => {
    return createSelector(
      [getAllCodeTables],
      (codeTables) => (codeTables[codeTableId] || []) as MetadataCodeTypeOf<T>[]
    );
  }
);

export const getCodeTableDictionary = memoize(
  <T extends CodeTableNumber>(id: T) => {
    return createSelector(
      [getCodeTable(id)],
      (codeTable): MetadataCodeRecordWithAdditionalPropertiesDictionary<T> => {
        return (
          codeTable.reduce(
            (dictionary, code) => {
              const { AdditionalProperties, ...item } = code;

              dictionary[item.Value] = {
                ...item,
                AdditionalProperties: AdditionalProperties?.reduce(
                  (additionalPropertiesObj, additionalProperty) => {
                    //  @ts-ignore TODO refactor this
                    if (additionalProperty.Value === 'True') {
                      //  @ts-ignore TODO refactor this
                      additionalPropertiesObj[additionalProperty.Key] = true;
                      //  @ts-ignore TODO refactor this
                    } else if (additionalProperty.Value === 'False') {
                      //  @ts-ignore TODO refactor this
                      additionalPropertiesObj[additionalProperty.Key] = false;
                    } else {
                      additionalPropertiesObj[additionalProperty.Key] =
                        additionalProperty.Value;
                    }
                    return additionalPropertiesObj;
                  },
                  {} as CodeTableAdditionalPropertyTypeOf<T>
                )
              };
              return dictionary;
            },
            {} as Record<
              string,
              Omit<MetadataCodeTypeOf<T>, 'AdditionalProperties'> & {
                AdditionalProperties: CodeTableAdditionalPropertyTypeOf<T>;
              }
            >
          ) || {}
        );
      }
    );
  }
);

type CodesMetadataDictionary = Record<
  CodeTableNumber,
  Record<
    string,
    Omit<MetadataCodeTypeOf<CodeTableNumber>, 'AdditionalProperties'> & {
      AdditionalProperties: CodeTableAdditionalPropertyTypeOf<CodeTableNumber>;
    }
  >
>;

export const getCodeTablesMap = memoize(
  (codeTableIds: CodeTableNumber[] = []) => {
    return createSelector(
      codeTableIds.map((id) => getCodeTableDictionary(id)),
      (...allCodes) => {
        const codeTablesMap: Partial<CodesMetadataDictionary> = {};

        codeTableIds.forEach((codeTableId, index) => {
          codeTablesMap[codeTableId] = allCodes[index];
        });

        return codeTablesMap;
      }
    );
  }
);

export const getCodeTableAsLabelValueArray = memoize(
  <T extends CodeTableNumber>(id: T, codeTableColumnValue?: string) => {
    return createSelector([getCodeTable(id)], (codeTable) => {
      return codeTable.map((item) => {
        let columnValue: any;
        if (
          codeTableColumnValue &&
          !DEFAULT_CODE_TABLE_COLUMN_VALUES.includes(codeTableColumnValue)
        ) {
          columnValue = (item as any)[codeTableColumnValue];

          if (!columnValue && item.AdditionalProperties.length) {
            Object.values(item.AdditionalProperties).find((property) => {
              if (property.Key === codeTableColumnValue) {
                columnValue = property.Value;
              }
              return columnValue;
            });
          }
        } else {
          columnValue = item.Value;
        }
        return {
          label: item.Name,
          value: columnValue
        };
      });
    });
  },
  (...args) => values(args).join('_')
);

export const CodeTableAdditionalPropertyDictionarySelector = memoize(
  (id: CodeTableNumber) => {
    return createSelector([getCodeTableDictionary(id)], (codeTable) => {
      return pluck(Object.values(codeTable), 'AdditionalProperties');
    });
  }
);

export const CodeTableBusinessUnitOrGlobalDictionarySelector = memoize(
  <T extends CodeTableNumber>(id: T) => {
    return createSelector([getCodeTableDictionary(id)], (codeTable) => {
      let globalConfig:
        | MetadataCodeWithAdditionalPropertiesDictionary<T>
        | undefined;
      let buConfig:
        | MetadataCodeWithAdditionalPropertiesDictionary<T>
        | undefined;
      Object.values(codeTable).forEach((data) => {
        if (data.Global) {
          globalConfig = data;
        } else {
          buConfig = data;
        }
      });

      return buConfig || globalConfig;
    });
  }
);

export const IsDbssSelector = createSelector(
  [getCodeTable(CodeTableNumber.ConvergentBillerConfigurationDetail)],
  (dbss) => !!dbss?.length
);

export const getUomWithFormattedLabelDictionarySelector = memoize(
  (
    filterThresholdType?: boolean,
    displayOnlyShortName?: boolean,
    balanceUnitId?: number,
    showGreaterUomValue?: boolean
  ) => {
    return createSelector(
      [getCodeTableDictionary(CodeTableNumber.UnitsOfMeasure)],
      (allUom) => {
        let uomList = Object.keys(allUom).length ? Object.values(allUom) : [];
        uomList = filterThresholdType
          ? uomList.filter((item) => {
              return !item.AdditionalProperties.hide_on_threshold_type;
            })
          : uomList;
        const categoryId =
          balanceUnitId &&
          allUom[balanceUnitId]?.AdditionalProperties?.category;

        if (showGreaterUomValue && balanceUnitId) {
          uomList =
            uomList.filter((unit) => {
              return categoryId && categoryId.includes('Data')
                ? (unit.Value === BYTE_ID ||
                    unit.AdditionalProperties.category === categoryId) &&
                    !(
                      unit.AdditionalProperties.conversion_factor >
                      allUom[balanceUnitId]?.AdditionalProperties
                        .conversion_factor
                    )
                : unit.Value === allUom[balanceUnitId]?.Value;
            }) || [];
        } else {
          uomList = categoryId
            ? uomList?.filter((uom) => {
                return categoryId.includes(BalanceUnitCategory.Data)
                  ? uom.AdditionalProperties?.category.includes(
                      BalanceUnitCategory.Data
                    )
                  : uom.AdditionalProperties?.category === categoryId;
              })
            : uomList;
        }
        return formatUomArrayItems(uomList, displayOnlyShortName);
      }
    );
  },
  (...args) => values(args).join('_')
);

export const ResidentialCustomerRequirementsSelector = createSelector(
  getCodeTableDictionary(CodeTableNumber.SubscriberRequirements),
  (subscriberRequirements) => {
    const requirementConfigList = Object.keys(subscriberRequirements).length
      ? Object.values(subscriberRequirements)
      : [];
    return getCreateSubscriberRequirements(requirementConfigList);
  }
);

export const CommercialCustomerRequirementsSelector = createSelector(
  getCodeTableDictionary(CodeTableNumber.SubscriberCategoryRequirements),
  (subscriberRequirements) => {
    const requirementConfigList = Object.keys(subscriberRequirements).length
      ? Object.values(subscriberRequirements)
      : [];
    return getCreateSubscriberRequirements(requirementConfigList);
  }
);
